import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence, Reorder } from "framer-motion";

const isGraded = (p) => p.status === "Graded";
const isReview = (p) => p.status === "Review" || p.status === "Manual Review";
const isPending = (p) => p.status === "Pending" || p.status === "Requested";
const isEvaluated = (p) => isGraded(p) || isReview(p);

/**
 * BulkAnnotationModal
 * Dedicated strictly to Blank Paper Annotation reordering and download.
 * Enables instructors to align the PDF page sequence with the physical paper stack
 * before printing onto physical answer booklets.
 * Supports Single Merged PDF or Separate PDFs (ZIP).
 */
const BulkAnnotationModal = ({
  isOpen,
  onClose,
  testId,
  testName = "Assessment",
  annotationPrintingOffsetConfigId,
  serverUrl,
  fetchWithAuth,
  swalSuccess,
  swalError,
  swalWarning,
}) => {
  const [allRawPapers, setAllRawPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [outputFormat, setOutputFormat] = useState("merged"); // "merged" | "zip"
  const [selectedTabId, setSelectedTabId] = useState("all");
  const [tabPapersMap, setTabPapersMap] = useState({});

  // Fetch all papers on modal open
  useEffect(() => {
    if (!isOpen || !testId) return;

    const fetchPapers = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/get-all/${testId}?all=true&minimal=true`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response || !response.ok) {
          const errData = await response?.json().catch(() => ({}));
          throw new Error(errData?.message || "Failed to fetch answer papers");
        }

        const resData = await response.json();
        const rawPapers = Array.isArray(resData)
          ? resData
          : resData.answerPapers || [];

        setAllRawPapers(rawPapers);
      } catch (err) {
        console.error("BulkAnnotationModal fetch error:", err);
        if (swalError) {
          swalError(err.message || "Failed to load answer papers.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPapers();
  }, [isOpen, testId, serverUrl, fetchWithAuth, swalError]);

  // Build batch tabs metadata
  const { tabs, initialTabPapers } = useMemo(() => {
    if (!allRawPapers || allRawPapers.length === 0) {
      return { tabs: [], initialTabPapers: {} };
    }

    // Identify all unique batch orders present
    const batchOrdersSet = new Set();
    let hasSingleUploads = false;

    allRawPapers.forEach((p) => {
      if (p.batchOrder != null) {
        batchOrdersSet.add(p.batchOrder);
      } else {
        hasSingleUploads = true;
      }
    });

    const sortedBatchOrders = Array.from(batchOrdersSet).sort((a, b) => a - b);

    // Universal sort helper for evaluated papers: scanOrder ASC (nulls last) -> createdAt ASC
    const sortPapers = (papers) => {
      return [...papers].sort((a, b) => {
        if (a.scanOrder != null && b.scanOrder != null) {
          return a.scanOrder - b.scanOrder;
        }
        if (a.scanOrder != null) return -1;
        if (b.scanOrder != null) return 1;
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
    };

    const tabList = [];
    const papersMap = {};

    // 1. All Papers Tab
    const allEvaluated = sortPapers(allRawPapers.filter(isEvaluated));
    const allGradedCount = allRawPapers.filter(isGraded).length;
    const allReviewCount = allRawPapers.filter(isReview).length;
    const allPendingCount = allRawPapers.filter(isPending).length;
    tabList.push({
      id: "all",
      label: "All Papers",
      batchOrder: null,
      evaluatedCount: allEvaluated.length,
      gradedCount: allGradedCount,
      reviewCount: allReviewCount,
      pendingCount: allPendingCount,
      isReady: allGradedCount > 0,
    });
    papersMap["all"] = allEvaluated;

    // 2. Individual Batch Tabs
    sortedBatchOrders.forEach((bOrder) => {
      const batchPapers = allRawPapers.filter((p) => p.batchOrder === bOrder);
      const batchEvaluated = sortPapers(batchPapers.filter(isEvaluated));
      const batchGradedCount = batchPapers.filter(isGraded).length;
      const batchReviewCount = batchPapers.filter(isReview).length;
      const batchPendingCount = batchPapers.filter(isPending).length;
      const tabId = `batch-${bOrder}`;

      tabList.push({
        id: tabId,
        label: `Batch #${bOrder}`,
        batchOrder: bOrder,
        evaluatedCount: batchEvaluated.length,
        gradedCount: batchGradedCount,
        reviewCount: batchReviewCount,
        pendingCount: batchPendingCount,
        isReady: batchGradedCount > 0,
      });
      papersMap[tabId] = batchEvaluated;
    });

    // 3. Single Uploads Tab (if any)
    if (hasSingleUploads) {
      const singlePapers = allRawPapers.filter((p) => p.batchOrder == null);
      const singleEvaluated = sortPapers(singlePapers.filter(isEvaluated));
      const singleGradedCount = singlePapers.filter(isGraded).length;
      const singleReviewCount = singlePapers.filter(isReview).length;
      const singlePendingCount = singlePapers.filter(isPending).length;
      const tabId = "single";

      tabList.push({
        id: tabId,
        label: "Single Uploads",
        batchOrder: null,
        isSingle: true,
        evaluatedCount: singleEvaluated.length,
        gradedCount: singleGradedCount,
        reviewCount: singleReviewCount,
        pendingCount: singlePendingCount,
        isReady: singleGradedCount > 0,
      });
      papersMap[tabId] = singleEvaluated;
    }

    return { tabs: tabList, initialTabPapers: papersMap };
  }, [allRawPapers]);

  // Sync initial tab papers map when data changes
  useEffect(() => {
    setTabPapersMap(initialTabPapers);

    if (tabs.length > 0) {
      const allTab = tabs.find((t) => t.id === "all");
      if (allTab && allTab.isReady) {
        setSelectedTabId("all");
      } else {
        const firstReadyBatch = tabs.find((t) => t.id !== "all" && t.isReady);
        setSelectedTabId(firstReadyBatch ? firstReadyBatch.id : "all");
      }
    }
  }, [tabs, initialTabPapers]);

  // Current active tab object
  const rawActiveTab = tabs.find((t) => t.id === selectedTabId) || tabs[0] || {
    id: "all",
    label: "All Papers",
    isReady: false,
    evaluatedCount: 0,
    gradedCount: 0,
    reviewCount: 0,
    pendingCount: 0,
  };

  const currentPapers = tabPapersMap[selectedTabId] || [];
  const initialPapersForTab = initialTabPapers[selectedTabId] || [];
  const excludedCount = Math.max(0, initialPapersForTab.length - currentPapers.length);

  const activeGradedCount = currentPapers.filter(isGraded).length;
  const activeReviewCount = currentPapers.filter(isReview).length;
  const activePendingCount = rawActiveTab.pendingCount;

  const activeTab = {
    ...rawActiveTab,
    evaluatedCount: currentPapers.length,
    gradedCount: activeGradedCount,
    reviewCount: activeReviewCount,
    isReady: activeGradedCount > 0,
    hasPendingOrReview: activeReviewCount > 0 || activePendingCount > 0,
  };

  const handleExcludePaper = (paperId) => {
    setTabPapersMap((prev) => ({
      ...prev,
      [selectedTabId]: (prev[selectedTabId] || []).filter((p) => p._id !== paperId),
    }));
  };

  const handleRestoreCurrentTab = () => {
    setTabPapersMap((prev) => ({
      ...prev,
      [selectedTabId]: initialPapersForTab,
    }));
  };

  const handleReorderCurrent = (reordered) => {
    setTabPapersMap((prev) => ({
      ...prev,
      [selectedTabId]: reordered,
    }));
  };

  const moveItem = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= currentPapers.length) return;
    const updated = [...currentPapers];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    handleReorderCurrent(updated);
  };

  const handleGenerate = async () => {
    const gradedPapers = currentPapers.filter(isGraded);
    if (gradedPapers.length === 0) {
      if (swalWarning) {
        swalWarning("No graded answer papers available in this selection.");
      }
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        answerPaperIds: gradedPapers.map((p) => p._id),
        type: "annotation",
        blankPaper: true,
        format: outputFormat, // "merged" or "zip"
        ...(annotationPrintingOffsetConfigId
          ? { annotationPrintingOffsetConfigId }
          : {}),
      };

      const response = await fetchWithAuth(
        `${serverUrl}/api/score/generate-bulk-report/${testId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.message || "Failed to generate bulk annotation.");
      }

      const blob = await response.blob();
      let filename = "";
      const disposition = response.headers?.get("content-disposition");
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      if (!filename) {
        const safeTestName = String(testName || "Assessment").replace(/[^a-zA-Z0-9_-]/g, "_");
        const batchSuffix = activeTab.batchOrder != null ? `_Batch_${activeTab.batchOrder}` : "";
        const ext = outputFormat === "zip" ? "zip" : "pdf";
        filename = `Bulk_Blank_Annotations_${safeTestName}${batchSuffix}.${ext}`;
      }

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);

      if (swalSuccess) {
        swalSuccess("Download Started", `${filename} is being downloaded.`);
      }
      onClose();
    } catch (err) {
      console.error("handleGenerate bulk blank annotation error:", err);
      if (swalError) {
        swalError(err.message || "Failed to generate bulk blank annotation.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="bulk-annotation-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget && !isGenerating) {
            onClose();
          }
        }}
      >
        <motion.div
          className="bulk-annotation-modal"
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ duration: 0.2 }}
        >
          {/* Header */}
          <div className="bulk-annotation-header">
            <div className="bulk-annotation-header-left">
              <div
                className="bulk-annotation-header-icon"
                style={{
                  backgroundColor: "#eff6ff",
                  color: "var(--color-2, #3b82f6)",
                  borderColor: "rgba(59, 130, 246, 0.3)",
                }}
              >
                <i className="fa-solid fa-arrows-up-down" />
              </div>
              <div>
                <h3 className="bulk-annotation-title">
                  Reorder Papers (Annotation on Blank Paper)
                </h3>
                <p className="bulk-annotation-subtitle">
                  Select a batch or export all papers. Drag items to adjust their sequence to match the physical paper stack.
                </p>
              </div>
            </div>

            <button
              className="bulk-annotation-close-btn"
              onClick={onClose}
              disabled={isGenerating}
              title="Close"
            >
              &times;
            </button>
          </div>

          {/* Batch Selector Tabs */}
          {tabs.length > 1 && (
            <div className="bulk-annotation-tabs">
              {tabs.map((tab) => {
                const isActive = tab.id === selectedTabId;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`bulk-annotation-tab ${isActive ? "active" : ""}`}
                    onClick={() => setSelectedTabId(tab.id)}
                    disabled={isGenerating}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`bulk-annotation-tab-pill ${
                        tab.isReady ? "ready" : "pending"
                      }`}
                    >
                      {tab.gradedCount} Graded
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Output Format Switcher Bar */}
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid var(--gray-200, #e2e8f0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Output Format:
              </span>
              <div
                style={{
                  display: "inline-flex",
                  background: "#e2e8f0",
                  padding: "3px",
                  borderRadius: "8px",
                  gap: "4px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOutputFormat("merged")}
                  disabled={isGenerating}
                  style={{
                    padding: "5px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: outputFormat === "merged" ? 600 : 500,
                    backgroundColor: outputFormat === "merged" ? "#ffffff" : "transparent",
                    color: outputFormat === "merged" ? "var(--color-2, #3b82f6)" : "#64748b",
                    border: outputFormat === "merged" ? "1px solid #cbd5e1" : "none",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    boxShadow: outputFormat === "merged" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  <i className="fa-regular fa-file-pdf" />
                  Single Merged PDF
                </button>
                <button
                  type="button"
                  onClick={() => setOutputFormat("zip")}
                  disabled={isGenerating}
                  style={{
                    padding: "5px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: outputFormat === "zip" ? 600 : 500,
                    backgroundColor: outputFormat === "zip" ? "#ffffff" : "transparent",
                    color: outputFormat === "zip" ? "var(--color-2, #3b82f6)" : "#64748b",
                    border: outputFormat === "zip" ? "1px solid #cbd5e1" : "none",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    boxShadow: outputFormat === "zip" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  <i className="fa-solid fa-file-zipper" />
                  Separate PDFs (.ZIP)
                </button>
              </div>
            </div>

            <span style={{ fontSize: "12px", color: "#64748b" }}>
              {outputFormat === "merged"
                ? "Continuous PDF for batch printer paper tray"
                : "ZIP folder containing individual student PDFs"}
            </span>
          </div>

          {/* Informative Banner if Active Tab has Pending or Review Papers */}
          {activeTab.hasPendingOrReview && (
            <div
              className="bulk-annotation-warning-banner"
              style={{
                backgroundColor: "#fef3c7",
                borderColor: "#f59e0b",
                color: "#92400e",
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.85rem",
              }}
            >
              <i className="fa-solid fa-circle-info" />
              <span>
                {activeReviewCount > 0 && `${activeReviewCount} paper(s) in review. `}
                {activePendingCount > 0 && `${activePendingCount} paper(s) pending evaluation. `}
                Only the <strong>{activeGradedCount} graded paper(s)</strong> will be generated.
              </span>
            </div>
          )}

          {/* Sub-bar just before the list */}
          <div className="bulk-annotation-meta-bar">
            <span className="bulk-annotation-hint-text">
              <i className="fa-solid fa-hand-pointer" /> Drag papers or use arrows to adjust printing sequence
            </span>

            <div className="bulk-annotation-top-right-meta">
              {excludedCount > 0 && (
                <button
                  type="button"
                  className="bulk-annotation-restore-btn"
                  onClick={handleRestoreCurrentTab}
                  disabled={isGenerating}
                  title="Restore excluded papers"
                >
                  <i className="fa-solid fa-rotate-left" /> Restore Excluded ({excludedCount})
                </button>
              )}
              <span className="bulk-annotation-total-badge">
                Selected: <strong>{currentPapers.length}</strong> ({activeGradedCount} Graded)
              </span>
            </div>
          </div>

          {/* Body: Drag & Drop Reorderable List */}
          <div className="bulk-annotation-body">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  color: "var(--gray-400)",
                  gap: "10px",
                }}
              >
                <i
                  className="fa-solid fa-spinner fa-spin"
                  style={{ fontSize: "28px", color: "var(--color-2, #3b82f6)" }}
                />
                <span style={{ fontSize: "0.9rem" }}>Loading papers...</span>
              </div>
            ) : currentPapers.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "180px",
                  color: "var(--gray-400)",
                  gap: "8px",
                }}
              >
                <i
                  className="fa-solid fa-file-circle-xmark"
                  style={{ fontSize: "32px", color: "var(--gray-300)" }}
                />
                <span style={{ fontSize: "0.92rem", fontWeight: 500 }}>
                  No papers available for {activeTab.label}.
                </span>
                {excludedCount > 0 && (
                  <button
                    type="button"
                    className="bulk-annotation-restore-btn"
                    onClick={handleRestoreCurrentTab}
                    disabled={isGenerating}
                    style={{ marginTop: "6px" }}
                  >
                    <i className="fa-solid fa-rotate-left" /> Restore Excluded Papers ({excludedCount})
                  </button>
                )}
              </div>
            ) : (
              <Reorder.Group
                axis="y"
                values={currentPapers}
                onReorder={handleReorderCurrent}
                className="bulk-annotation-reorder-list"
              >
                {currentPapers.map((paper, index) => {
                  const studentName =
                    `${paper.firstName || ""} ${paper.lastName || ""}`.trim() ||
                    paper.studentName ||
                    "—";
                  const rollNo = paper.rollNo;
                  const referenceId = paper.referenceId;
                  const graded = paper.status === "Graded";

                  return (
                    <Reorder.Item
                      key={paper._id}
                      value={paper}
                      className="bulk-annotation-item"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                        delay: index * 0.02,
                      }}
                      whileDrag={{
                        scale: 1.02,
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12)",
                        backgroundColor: "#f0f7ff",
                        borderColor: "var(--color-2, #3b82f6)",
                        zIndex: 50,
                        cursor: "grabbing",
                      }}
                      dragTransition={{ bounceStiffness: 600, bounceDamping: 35 }}
                      style={{ cursor: "grab" }}
                    >
                      {/* Left: Drag Handle + Index + Student Information */}
                      <div className="bulk-annotation-item-left">
                        <div className="bulk-annotation-drag-handle">
                          <i className="fa-solid fa-grip-vertical" />
                        </div>

                        <span className="bulk-annotation-index">{index + 1}</span>

                        <div className="bulk-annotation-details">
                          <div className="bulk-annotation-title-row">
                            <span className="bulk-annotation-student-name" title={studentName}>
                              {studentName}
                            </span>

                            {paper.batchOrder != null ? (
                              <span className="bulk-annotation-batch-tag">
                                Batch #{paper.batchOrder}
                              </span>
                            ) : (
                              <span className="bulk-annotation-single-tag">
                                Single Upload
                              </span>
                            )}

                            {paper.scanOrder != null && (
                              <span
                                className="bulk-annotation-scan-tag"
                                title="Original scan order from upload"
                              >
                                Scan #{paper.scanOrder}
                              </span>
                            )}
                          </div>

                          <div className="bulk-annotation-sub-row">
                            {referenceId && (
                              <span>
                                Ref ID: <strong>{referenceId}</strong>
                              </span>
                            )}

                            {referenceId && rollNo && <span>•</span>}
                            {rollNo && (
                              <span>
                                Roll: <strong>{rollNo}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Status Pill + Chevron Step Controls */}
                      <div className="bulk-annotation-item-right">
                        <span
                          className={`bulk-annotation-status-pill ${
                            graded ? "graded" : "review"
                          }`}
                        >
                          {paper.status}
                        </span>

                        <div className="bulk-annotation-step-btns">
                          <button
                            type="button"
                            className="bulk-annotation-step-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveItem(index, index - 1);
                            }}
                            disabled={index === 0 || isGenerating}
                            title="Move Up"
                          >
                            <i className="fa-solid fa-chevron-up" />
                          </button>
                          <button
                            type="button"
                            className="bulk-annotation-step-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveItem(index, index + 1);
                            }}
                            disabled={index === currentPapers.length - 1 || isGenerating}
                            title="Move Down"
                          >
                            <i className="fa-solid fa-chevron-down" />
                          </button>
                        </div>

                        <button
                          type="button"
                          className="bulk-annotation-exclude-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExcludePaper(paper._id);
                          }}
                          disabled={isGenerating}
                          title="Exclude paper from this bulk blank annotation"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bulk-annotation-footer">
            <button
              type="button"
              className="bulk-annotation-btn-cancel"
              onClick={onClose}
              disabled={isGenerating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="bulk-annotation-btn-generate"
              onClick={handleGenerate}
              disabled={loading || isGenerating || activeGradedCount === 0}
              style={{
                backgroundColor: "var(--color-2, #3b82f6)",
                borderColor: "var(--color-2, #3b82f6)",
              }}
              title={
                activeGradedCount === 0
                  ? "No graded papers available to generate"
                  : `Generate ${outputFormat === "zip" ? "ZIP archive" : "single merged PDF"} for ${activeTab.label}`
              }
            >
              {isGenerating ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Compiling & Generating {outputFormat === "zip" ? "ZIP" : "PDF"}...
                </>
              ) : (
                <>
                  <i className={outputFormat === "zip" ? "fa-solid fa-file-zipper" : "fa-solid fa-file-arrow-down"} />
                  Generate {outputFormat === "zip" ? "ZIP" : "PDF"} - {activeTab.label} ({activeGradedCount} Graded)
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BulkAnnotationModal;
